/**
 * Utility to check SAS token expiry from Azure Blob Storage URLs.
 *
 * Azure SAS tokens contain a `se` (signed expiry) query parameter in ISO 8601
 * format. This module parses that parameter and determines whether the token
 * is still usable, applying a configurable safety buffer so we don't start a
 * fetch that would fail mid-flight.
 */

/** Safety buffer in milliseconds — treat token as expired this much before actual expiry */
const EXPIRY_BUFFER_MS = 30_000; // 30 seconds

/**
 * Checks whether the SAS token embedded in a blob storage URL has expired
 * (or will expire within the safety buffer window).
 *
 * @param url - Full Azure Blob Storage URL with SAS query string
 * @returns `true` if the token is expired / about to expire / unparseable
 */
export function isSasTokenExpired(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    const signedExpiry = parsedUrl.searchParams.get('se');

    if (!signedExpiry) {
      // No SAS expiry parameter — cannot determine validity, treat as expired
      return true;
    }

    const expiryTime = new Date(signedExpiry).getTime();
    if (isNaN(expiryTime)) {
      return true;
    }

    return Date.now() + EXPIRY_BUFFER_MS >= expiryTime;
  } catch {
    // URL parsing failed — treat as expired to be safe
    return true;
  }
}

/**
 * Returns `true` if every SAS URL on the given albums is still fresh (or
 * absent). Returns `false` as soon as any `thumbnailUrl` or `mediumUrl` is
 * expired or about to expire — the caller should treat the cached data as
 * unusable for revalidation (i.e. skip `If-None-Match`) and force a fresh
 * fetch so the server returns new SAS tokens.
 */
export function hasFreshSasUrls(
  albums: ReadonlyArray<{ thumbnailUrl?: string | null; mediumUrl?: string | null }>
): boolean {
  for (const album of albums) {
    if (album.thumbnailUrl && isSasTokenExpired(album.thumbnailUrl)) return false;
    if (album.mediumUrl && isSasTokenExpired(album.mediumUrl)) return false;
  }
  return true;
}
